import { useCallback } from 'react';
import { pick } from 'ramda';
import { throttle } from 'underscore';
import { useDispatch } from 'react-redux';
import { useIsAdmin } from 'js/hooks/user';
import * as urlUtil from 'js/utils/url.util';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'js/hooks/i18n';
import { useShowLogin } from 'js/hooks/useShowLogin';
import { storageService } from 'js/services/storage.service';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { NEED_VERIFICATION_STATUSES } from 'js/components/popups/DataVerificationStatusPopup/constants';

import * as Types from '../types';
import * as helpers from '../helpers';
import { GAME_LAUNCH_TYPE } from '../constants';
import { useGetGameConfig } from './useGetGameConfig';
import { useLaunchGameByCasino } from './useLaunchGameByCasino';
import { useHandleSuccessLogin } from './useHandleSuccessLogin';
import { useNavigateFromLaunchGamePath } from './useNavigateFromLaunchGamePath';
import { useLaunchWithLimitsVerification } from './useLaunchWithLimitsVerification';

export const useLaunchGame = () => {
    const isAdmin = useIsAdmin();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const showLogin = useShowLogin();
    const { isLoggedIn, userDetails: { ageVerificationStatus } } = useUserSelector(pick([
        'isLoggedIn', 'userDetails'
    ]));
    const translate = useTranslations('playtech.mobile.system.launcher.gameLaunchPopup.');
    const navigateFromLaunchGamePath = useNavigateFromLaunchGamePath();
    const getGameConfig = useGetGameConfig();
    const handleSuccessLogin = useHandleSuccessLogin();
    const launchGameByCasino = useLaunchGameByCasino();
    const launchWithLimitsVerification = useLaunchWithLimitsVerification();
    const { enableDataVerificationStatusCheck, loginPage } = useAppSelector(pick([
        'enableDataVerificationStatusCheck', 'loginPage'
    ]));
    const isDataVerificationStatusNeeded = enableDataVerificationStatusCheck
        && ageVerificationStatus
        && NEED_VERIFICATION_STATUSES.includes(ageVerificationStatus);

    const launchGame = useCallback((launchOptions: Types.ILaunchGameOptions): void => {
        const {
            gameLaunchType,
            code: gameCode,
            mode: propsMode,
            queryString: additionalQueryString,
            ...options
        } = launchOptions;

        if (isAdmin) {
            dispatch(showCommonPopupAction({
                title: translate('title'),
                content: translate('content')
            }));

            return;
        }

        if (isDataVerificationStatusNeeded) {
            console.warn('Game playing is disabled because of "DataVerificationStatus" verification is needed');

            return;
        }

        if (gameLaunchType === GAME_LAUNCH_TYPE.CASINO) {
            launchGameByCasino(launchOptions);

            return;
        }

        const gameConfig = getGameConfig(gameCode);

        if (!gameConfig) {
            navigateFromLaunchGamePath();

            return;
        }

        const mode = helpers.getActualMode({
            isLoggedIn,
            mode: propsMode,
            funSupport: gameConfig.funSupport
        });

        options.gameName = options.gameName || gameConfig.name;

        if (mode && !isLoggedIn) {
            if (loginPage) {
                storageService.session.setItem('gamesPageUrl', urlUtil.getCleanUrl(pathname));
            }

            showLogin({
                afterLoginUrl: `/igaming/${gameCode}`,
                onLogin: () => handleSuccessLogin({ gameCode, mode, additionalQueryString, ...options })
            });

            return;
        }

        launchWithLimitsVerification({ gameCode, mode, additionalQueryString, ...options });
    }, [
        translate, dispatch, isAdmin, launchGameByCasino, loginPage,
        isLoggedIn, showLogin, navigateFromLaunchGamePath, isDataVerificationStatusNeeded,
        getGameConfig, handleSuccessLogin, launchWithLimitsVerification, pathname
    ]);

    return throttle((launchOptions: Types.ILaunchGameOptions) => launchGame(launchOptions), 500);
};
